import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../screens/FormEditorPage.css';
import { baseUrl } from '../utils/config';
import {
    Box,
    Button,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Helper function to get auth headers (assuming token in localStorage)
const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const ViewSubmittedFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [editedData, setEditedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const theme = useTheme();

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${baseUrl}/api/submitted-savedforms/${id}`, {
            headers: getAuthHeaders()
        });

        setFormData(response.data);
        setEditedData(response.data.formData.map(row => ({
          ...row,
          cells: Array.isArray(row.cells) ? row.cells.map(cell => (cell ? { ...cell } : null)) : []
        })));

        if (response.data.formTemplateId && response.data.formTemplateId.associatedImage) {
          setImagePreviewUrl(`${baseUrl}${response.data.formTemplateId.associatedImage}`);
        } else {
          setImagePreviewUrl('');
        }

        console.log('Fetched Form Data:', response.data);
      } catch (err) {
        console.error('Error fetching form data:', err);
        if (axios.isAxiosError(err) && err.response) {
          if (err.response.status === 401 || err.response.status === 403) {
            setError('You are not authorized to view this form. Please log in again.');
          } else if (err.response.status === 404) {
            setError('Form not found. It might have been deleted or the ID is incorrect.');
          } else if (err.response.status === 400) {
            setError('Invalid form ID provided.');
          } else {
            setError(`Failed to load form data. Error: ${err.response.status} ${err.response.statusText}. Please check your network and try again.`);
          }
        } else {
          setError('Failed to load form data. Please check your network and try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchFormData();
    }
  }, [id]);

  const handleCellChange = (rowIndex, cellIndex, value) => {
    setEditedData(prevEditedData => {
      const updatedData = [...prevEditedData];
      if (updatedData[rowIndex] && updatedData[rowIndex].cells && updatedData[rowIndex].cells[cellIndex]) {
        updatedData[rowIndex].cells[cellIndex] = {
          ...updatedData[rowIndex].cells[cellIndex],
          value: value
        };
      } else {
        console.warn(`Attempted to edit a non-existent cell at [${rowIndex}, ${cellIndex}]`);
      }
      return updatedData;
    });
  };

  const handleViewImage = () => {
    if (imagePreviewUrl) {
      window.open(imagePreviewUrl, 'ImagePopUp', 'width=800,height=600,resizable=yes,scrollbars=yes');
    } else {
      alert('No image associated with this form.');
    }
  };

  if (loading) {
    return (
      <div className="form-editor-container">
        <p>Loading form data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-editor-container error-message">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="cancel-button" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  if (!formData || !formData.formData || formData.formData.length === 0) {
    return (
      <div className="form-editor-container">
        <p>No form data available for viewing.</p>
        <button className="cancel-button" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: theme.spacing(4),
        boxSizing: 'border-box',
      }}
    >
      <div className="form-editor-container"
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          marginTop: '70px',
          alignItems: 'center',
          overflowY: 'auto',
          padding: theme.spacing(2),
          boxSizing: 'border-box',
          backgroundColor: '#fff',
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.shadows[3],
        }}
      >
       <h1 className="view-form-heading">
        <span className="form-number-display">
          Form Number: {formData.formNumber || 'N/A'}
        </span>
        <span className="form-name-display">
          - Form Name: {formData.formName || formData.formTemplateId?.formName || 'Untitled Form'}
        </span>
      </h1>

        {/* Display Image Preview above the table */}
        {/* {imagePreviewUrl && (
          <Box sx={{ my: 2, textAlign: 'center', width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>Associated Image:</Typography>
            <img
              src={imagePreviewUrl}
              alt="Associated Form"
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                height: 'auto',
                display: 'block',
                margin: '0 auto',
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[1],
              }}
            />
          </Box>
        )} */}

        {/* View Image Button (now primarily for the pop-up) */}
        {imagePreviewUrl && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleViewImage}
            sx={{ mb: 2 }}
          >
            View Image in Pop-up
          </Button>
        )}

        {/* Render the table */}
        <div className="table-editable-container" style={{ width: '100%', overflowX: 'auto' }}>
          <table>
            <tbody>
              {editedData.map((rowObj, rowIndex) => (
                <tr key={`data-row-${rowIndex}`} className={rowObj.isRowBold ? 'bold-row' : ''}>
                  {Array.isArray(rowObj.cells) && rowObj.cells.map((cell, cellIndex) => {
                    if (cell === null || (cell.mergeRange && !cell.isMergedTopLeft)) {
                      return null;
                    }

                    const cellStyle = {
                      fontWeight: cell.isCellBold ? 'bold' : 'normal',
                      backgroundColor: (cell.fillColor && cell.fillColor === "#FF00") ? '#C5FFF8' : cell.fillColor,
                      color: cell.fontColor || 'inherit',
                    };

                    return (
                      cell ? (
                        <td
                            key={`data-cell-${rowIndex}-${cellIndex}`}
                            colSpan={cell.colspan || 1}
                            rowSpan={cell.rowspan || 1}
                            style={cellStyle}
                        >
                            <input
                                type="text"
                                value={cell.value ? String(cell.value).replace('[ASSIGNEE]', '') : ''}
                                onChange={(e) => handleCellChange(rowIndex, cellIndex, e.target.value)}
                                disabled
                                readOnly
                            />
                        </td>
                    ) : (
                        <td key={`data-cell-${rowIndex}-${cellIndex}`}></td>
                    )
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="form-actions"
           style={{
             marginTop: theme.spacing(3),
             marginBottom: theme.spacing(3) // ADDED THIS LINE FOR SPACE BELOW THE BUTTON
           }}>
        <button className="cancel-button" onClick={() => navigate(-1)}>
          Back to List
        </button>
      </div>
    </div>
    </Box>
  );
};

export default ViewSubmittedFormPage;